import { useUnmount } from 'ahooks';
import { useCallback, useEffect, useRef, useState } from 'react';

const isBrowser = typeof document !== 'undefined';
function isOnline(): boolean {
	if (isBrowser && typeof navigator.onLine !== 'undefined') {
		return navigator.onLine;
	}
	return true;
}

type Listener = (active: boolean) => void;
const listeners: Listener[] = [];

function subscribe(listener: Listener) {
	listeners.push(listener);
	return function unsubscribe() {
		const index = listeners.indexOf(listener);
		if (index > -1) {
			listeners.splice(index, 1);
		}
	};
}

let windowActive = true;

if (isBrowser) {
	const revalidate = () => {
		windowActive = isDocumentVisible() && isOnline();
		for (let i = 0; i < listeners.length; i++) {
			const listener = listeners[i];
			listener(windowActive);
		}
	};
	window.addEventListener('visibilitychange', revalidate, false);
	window.addEventListener('focus', revalidate, false);
  window.addEventListener('online', revalidate, false);
  window.addEventListener('offline', revalidate, false);
}

function isDocumentVisible(): boolean {
	if (isBrowser) {
		return document.visibilityState !== 'hidden';
	}
	return true;
}

export default function useFocus() {
	const [state, setState] = useState(windowActive);
	const unsubscribeRef = useRef<() => void>();

	const stopSubscribe = useCallback(() => {
		unsubscribeRef.current?.();
	}, []);

	useEffect(() => {
		unsubscribeRef.current = subscribe(setState);
		return () => {
			stopSubscribe();
		};
	}, [stopSubscribe]);

	useUnmount(stopSubscribe);

	return state;
}
